import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Input, Subtitle1 } from "@fluentui/react-components"
import { FC, useState, useContext } from "react";
import { Tender, TenderContact } from "../../../models/tendering";
import { validateEmail } from "../../../utils/validate-email";
import { validateNumbersOnly } from "../../../utils/validate-numbers-only";
import { deepCloneObject } from "../../../utils/clone";
import { useAppContext } from "../../../AppContext";
import apiClient from "../../../services/api";
import { updateTenderDocument } from "../../../services/tenderingService";
import { createDocument, getDocument } from "../../../services/documentService";
import { ContactCode } from "./ContactCode";
import { DismissRegular } from "@fluentui/react-icons";

interface ContactChangeProps {
  tender: Tender;
  editMode: 'email' | 'phone';
  open: boolean;
  onClose: () => void;
  onSaved?: (updatedTender: Tender) => void;
}

const ContactChange: FC<ContactChangeProps> = ({ tender, editMode, open, onClose, onSaved }) => {
  const { user } = useAppContext();
  
  // Local state for edited contact info
  const [email, setEmail] = useState(tender.contact?.email || '');
  const [phone, setPhone] = useState(tender.contact?.phone || '');
  
  // Validation state
  const [emailValid, setEmailValid] = useState(true);
  const [phoneValid, setPhoneValid] = useState(true);
  
  // Loading state
  const [emailLoading, setEmailLoading] = useState(false);
  const [phoneLoading, setPhoneLoading] = useState(false);
  
  // Code dialog state for non-system users
  const [showCodeDialog, setShowCodeDialog] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [currentSavingField, setCurrentSavingField] = useState<'email' | 'phone'>('email');

  // Store initial contact to compare changes
  const [initialContact] = useState<TenderContact>(
    deepCloneObject(tender.contact) || { email: '', phone: '' }
  );

  const updateTenderContact = async (field: 'email' | 'phone', value: string) => {
    try {
      const updatedTender = deepCloneObject(tender);
      
      if (!updatedTender.contact) {
        updatedTender.contact = { email: '', phone: '' };
      }
      
      // Update with the new value for the specific field
      if (field === 'email') {
        updatedTender.contact.email = value;
      } else {
        updatedTender.contact.phone = value;
      }
      
      await updateTenderDocument(tender.tenderID, updatedTender);
      
      return updatedTender;
    } catch (error) {
      console.error('Error updating tender contact:', error);
      throw error;
    }
  };

  const saveContact = async (field: 'email' | 'phone') => {
    const isEmail = field === 'email';
    const value = isEmail ? email : phone;
    const initialValue = isEmail ? initialContact.email : initialContact.phone;
    
    // Check if there's actually a change
    if (value === initialValue) {
      return false;
    }
    
    // Validation
    const isValid = isEmail ? validateEmail(value) : validateNumbersOnly(value);
    if (!isValid) {
      isEmail ? setEmailValid(false) : setPhoneValid(false);
      return false;
    }
    
    isEmail ? setEmailValid(true) : setPhoneValid(true);
    isEmail ? setEmailLoading(true) : setPhoneLoading(true);
    setCurrentSavingField(field);

    try {
      // Create document record for the change
      const documentData = {
        moduleID: tender.tenderID,
        notificationType: isEmail ? 1 : 2,
        question: `Förfrågningar kontaktuppgifter (${isEmail ? 'e-post' : 'mobiltelefon'})`,
        new: { 
          value: value,
          question: `Förfrågningar kontaktuppgifter (${isEmail ? 'e-post' : 'mobiltelefon'})`
        },
        old: { 
          value: initialValue,
          question: `Förfrågningar kontaktuppgifter (${isEmail ? 'e-post' : 'mobiltelefon'})`
        },
        notifyUser: !user?.userType?.system
      };

      const docResponse = await createDocument(documentData);
      
      // Non-system users need verification code
      const docDetails = await getDocument(docResponse);
      // Backend code generation is currently disabled (DocumentService.CreateDocument), so no code may be returned
      if (docDetails?.code == null) {
        const updatedTender = await updateTenderContact(field, value);
        onSaved?.(updatedTender);
        onClose();
        return true;
      }
      setVerificationCode(String(docDetails.code));
      setShowCodeDialog(true);
      return false; // Don't update yet for non-system users
    } catch (error) {
      console.error('Error saving contact:', error);
      alert('Ett fel uppstod vid sparande av kontaktuppgifter');
      return false;
    } finally {
      isEmail ? setEmailLoading(false) : setPhoneLoading(false);
    }
  };

  const handleSave = async () => {
    await saveContact(editMode);
  };

  const isLoading = emailLoading || phoneLoading;
  const currentValue = editMode === 'email' ? email : phone;
  const initialValue = editMode === 'email' ? initialContact.email : initialContact.phone;
  const hasChanged = currentValue !== initialValue;
  const isValid = editMode === 'email' ? emailValid : phoneValid;

  return (
    <>
      <Dialog modalType="alert" open={open && !showCodeDialog} onOpenChange={(_, data) => !data.open && onClose()}>
        <DialogSurface>
          <DialogTitle
          action={
            <Button
              shape="circular"
              appearance="subtle"
              icon={<DismissRegular />}
              onClick={onClose}
              style={{ position: 'absolute', top: '20px', right: '20px' }}
            />
          }
          >
          <Subtitle1>
            {editMode === 'email' ? 'Redigera e-post' : 'Redigera telefonnummer'}
          </Subtitle1>
          </DialogTitle>
          <DialogBody>
            <DialogContent>
              {editMode === 'email' ? (
                <div className="py-4">
                  <Field 
                    label="E-post för mottagande av förfrågningar" 
                    required
                    validationMessage={!emailValid ? 'Ogiltig e-postadress' : undefined}
                    validationState={!emailValid ? 'error' : 'none'}
                  >
                    <Input 
                      value={email} 
                      onChange={(_, data) => {
                        setEmail(data.value);
                        setEmailValid(true);
                      }}
                      disabled={isLoading}
                    />
                  </Field>
                </div>
              ) : (
                <div className="py-4">
                  <Field 
                    label="Telefonnummer för mottagande av förfrågningar på sms (endast mobilnummer)" 
                    required
                    validationMessage={!phoneValid ? 'Ogiltigt telefonnummer (endast siffror)' : undefined}
                    validationState={!phoneValid ? 'error' : 'none'}
                  >
                    <Input 
                      value={phone} 
                      onChange={(_, data) => {
                        setPhone(data.value);
                        setPhoneValid(true);
                      }}
                      disabled={isLoading}
                    />
                  </Field>
                </div>
              )}
            </DialogContent>
          </DialogBody>
          <DialogActions className="py-2" style={{ flexShrink: 0 }}>
            <Button appearance="secondary" onClick={onClose} disabled={isLoading}>
              Avbryt
            </Button>
            <Button 
              appearance="primary" 
              onClick={handleSave} 
              disabled={!hasChanged || !isValid || isLoading}
            >
              {isLoading ? 'Sparar...' : 'Spara'}
            </Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>

      {/* Verification Code Dialog */}
      <ContactCode
        open={showCodeDialog}
        code={verificationCode}
        tender={tender}
        isEmail={editMode === 'email'}
        isPhone={editMode === 'phone'}
        newValue={editMode === 'email' ? email : phone}
        onClose={() => {
          setShowCodeDialog(false);
          onClose();
        }}
        onVerified={(updatedTender) => {
            // Code was correct, contact info was updated
            onSaved?.(updatedTender);
        }}
      />
    </>
  )
}

export default ContactChange;